import { Eye, RotateCcw, Upload } from "lucide-react";
import React, { useCallback, useEffect, useState } from "react";
import AppButton from "../../components/ui/AppButton";
import Pagination from "../../components/ui/dashboard/Pagination";
import Table, { Column } from "../../components/ui/dashboard/Table";
import ThreeDotsMenu from "../../components/ui/ThreeDotsMenu";
import {
  DispatchImportBatch,
  DispatchImportBatchSearchResponse,
} from "./@types";
import DispatchImportModal from "./components/import/DispatchImportModal";
import ImportBatchDetailsModal from "./components/import/ImportBatchDetailsModal";
import {
  formatDateRange,
  formatImportAmount,
  formatImportDate,
  ImportStatusBadge,
} from "./components/import/ImportShared";
import ReverseImportModal from "./components/import/ReverseImportModal";
import { useGetDispatchImportBatchesMutation } from "./dispatchApiSlice";

type ReverseTarget = { id: string; label?: string };

const DispatchImport: React.FC = () => {
  const [isImportOpen, setIsImportOpen] = useState(false);

  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);

  const [detailsBatchId, setDetailsBatchId] = useState<string | null>(null);
  const [reverseTarget, setReverseTarget] = useState<ReverseTarget | null>(
    null
  );

  const [
    getBatches,
    { data: batchesData, isLoading: isLoadingBatches, error: batchesError },
  ] = useGetDispatchImportBatchesMutation();

  const fetchBatches = useCallback(async () => {
    try {
      await getBatches({ pageNumber: currentPage, pageSize }).unwrap();
    } catch (err) {
      console.error("Failed to fetch import batches:", err);
    }
  }, [currentPage, pageSize, getBatches]);

  useEffect(() => {
    fetchBatches();
  }, [fetchBatches]);

  const refreshHistory = () => {
    if (currentPage === 0) {
      fetchBatches();
    } else {
      setCurrentPage(0);
    }
  };

  const columns: Column<DispatchImportBatch>[] = [
    {
      key: "fileName",
      header: "File",
      render: (value: string, item: DispatchImportBatch) => (
        <div className="min-w-0">
          <p className="text-hColor break-all">{value}</p>
          {item.sheetName && (
            <p className="text-[12px] text-pColor">{item.sheetName}</p>
          )}
        </div>
      ),
    },
    {
      key: "ranOn",
      header: "Ran on",
      render: (value: string, item: DispatchImportBatch) => (
        <div>
          <p className="text-hColor">{formatImportDate(value)}</p>
          {item.ranBy && (
            <p className="text-[12px] text-pColor">{item.ranBy}</p>
          )}
        </div>
      ),
    },
    {
      key: "tripDateFrom",
      header: "Trip dates",
      render: (_: string, item: DispatchImportBatch) => (
        <span className="text-hColor whitespace-nowrap">
          {formatDateRange(item.tripDateFrom, item.tripDateTo)}
        </span>
      ),
    },
    {
      key: "journeysImported",
      header: "Journeys",
      render: (value: number, item: DispatchImportBatch) => (
        <span className="text-hColor whitespace-nowrap">
          {value} / {item.journeysFound}
          {item.journeysFailed > 0 && (
            <span className="text-red-600"> · {item.journeysFailed} failed</span>
          )}
        </span>
      ),
    },
    {
      key: "errorCount",
      header: "Errors / warnings",
      render: (value: number, item: DispatchImportBatch) => (
        <span className="whitespace-nowrap">
          <span className={value ? "text-red-600" : "text-hColor"}>
            {value}
          </span>
          <span className="text-pColor"> / </span>
          <span
            className={item.warningCount ? "text-yellow-700" : "text-hColor"}
          >
            {item.warningCount}
          </span>
        </span>
      ),
    },
    {
      key: "grossIncome",
      header: "Gross income (NGN)",
      render: (value: number) => (
        <span className="text-green-600">{formatImportAmount(value)}</span>
      ),
    },
    {
      key: "netBanked",
      header: "Net banked (NGN)",
      render: (value: number) => (
        <span className="text-green-700">{formatImportAmount(value)}</span>
      ),
    },
    {
      key: "status",
      header: "Status",
      render: (value: string) => <ImportStatusBadge status={value} />,
    },
    {
      key: "actions",
      header: "Action",
      align: "center" as const,
      render: (_: any, item: DispatchImportBatch) => (
        <ThreeDotsMenu
          actions={[
            {
              label: "View details",
              onClick: () => setDetailsBatchId(item.id),
              icon: <Eye className="w-4 h-4" />,
            },
            ...(item.canBeReversed
              ? [
                  {
                    label: "Reverse import",
                    onClick: () =>
                      setReverseTarget({ id: item.id, label: item.fileName }),
                    icon: <RotateCcw className="w-4 h-4" />,
                  },
                ]
              : []),
          ]}
        />
      ),
    },
  ];

  const response = batchesData?.data as
    | DispatchImportBatchSearchResponse
    | undefined;

  return (
    <div className="min-h-screen py-6 w-full bg-[#F9F9F9] p-4">
      <div className="w-full mx-auto space-y-6">
        <div className="flex flex-wrap items-center justify-between gap-3 bg-white rounded-[10px] p-4">
          <div>
            <h2 className="text-xl font-semibold text-hColor">
              Dispatch import
            </h2>
            <p className="text-[13px] text-pColor">
              Bulk-create dispatches from loading sheet spreadsheets.
            </p>
          </div>
          <AppButton
            onClick={() => setIsImportOpen(true)}
            className="flex items-center px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/60 transition-colors min-w-[146px] h-[38px]"
          >
            <Upload className="h-4 w-4 mr-2" />
            Import dispatches
          </AppButton>
        </div>

        <div className="space-y-6 bg-white rounded-[10px] p-4">
          <h2 className="text-[16px] font-semibold text-hColor">
            Import history
          </h2>
          <div className="overflow-hidden">
            <Table
              data={response?.data || []}
              columns={columns}
              loading={isLoadingBatches}
              error={batchesError}
              emptyMessage="No imports yet"
            />
          </div>
        </div>

        {response && (
          <Pagination
            currentPage={response.currentPage}
            totalPages={response.totalPages}
            pageSize={response.pageSize}
            totalCount={response.totalCount}
            hasNextPage={response.hasNextPage}
            hasPreviousPage={response.hasPreviousPage}
            onPageChange={setCurrentPage}
            onPageSizeChange={(size) => {
              setPageSize(size);
              setCurrentPage(0);
            }}
          />
        )}
      </div>

      {isImportOpen && (
        <DispatchImportModal
          isOpen={isImportOpen}
          onClose={() => setIsImportOpen(false)}
          onImported={refreshHistory}
          onReverse={(id, fileName) => {
            setIsImportOpen(false);
            setReverseTarget({ id, label: fileName });
          }}
        />
      )}

      {detailsBatchId && (
        <ImportBatchDetailsModal
          isOpen={!!detailsBatchId}
          importBatchId={detailsBatchId}
          onClose={() => setDetailsBatchId(null)}
          onReverse={() => {
            const batch = response?.data.find((b) => b.id === detailsBatchId);
            setReverseTarget({ id: detailsBatchId, label: batch?.fileName });
            setDetailsBatchId(null);
          }}
        />
      )}

      {reverseTarget && (
        <ReverseImportModal
          isOpen={!!reverseTarget}
          importBatchId={reverseTarget.id}
          label={reverseTarget.label}
          onClose={() => setReverseTarget(null)}
          onReversed={fetchBatches}
        />
      )}
    </div>
  );
};

export default DispatchImport;
